import React from "react"
import { OrderStatusId } from "@phonero/common-graphql/types"
import { PuxIcon, useTranslation } from "@phonero/pux-react"
import css from "./SingleOrderStatusChecker.module.scss"
import { failedCircle, okCircle } from "@phonero/pux-icons/icons"
import classNames from "classnames"
import { pushOrderQueueItem } from "../util/useAppStateCommon"
import { OrderStatus } from "./OrderStatus"
import { useAppEvent } from "../Orders/CustomerOrderBadge"
import { useEffect, useState } from "react"

export const SingleOrderStatusChecker = ({
  orderId,
  error: submitOrderError,
  statusMessageOnCompleted,
  statusMessageOnCompletedGenerator,
  skipReFetchObservableQueries,
}: {
  orderId: string
  error?: boolean
  statusMessageOnCompleted?: string
  statusMessageOnCompletedGenerator?: () => string
  skipReFetchObservableQueries?: boolean | undefined
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    if (!orderId) return
    pushOrderQueueItem({
      id: orderId,
      skipReFetchObservableQueries: skipReFetchObservableQueries,
    })
  }, [orderId, skipReFetchObservableQueries])

  const submitOrderFailed = !orderId && submitOrderError
  const [statusError, setStatusError] = useState<string | undefined>()
  const [status, setStatus] = useState<OrderStatusId | undefined>()
  const checkStatusFailed = !!statusError
  const isCompleted = status === OrderStatusId.Completed
  const isCancelled = status === OrderStatusId.Cancelled

  useAppEvent("OrderCompleted", ({ detail: { id, status } }) => id === orderId && setStatus(statusForValue(status)))
  useAppEvent("StatusCheckFailed", ({ detail: { id, errorMessage } }) => id === orderId && setStatusError(errorMessage))

  return (
    <div style={{ marginBottom: "30px" }}>
      <div className={css.confirmedIconBox}>
        <div>
          {(submitOrderFailed || checkStatusFailed) && (
            <PuxIcon
              icon={failedCircle}
              color={submitOrderFailed ? "danger" : ""}
              className={classNames(css.icon, "icon-large", checkStatusFailed ? "icon-yellow" : "")}
            />
          )}
          {!(submitOrderFailed || checkStatusFailed) && !(isCompleted || isCancelled) && (
            <PuxIcon icon={okCircle} color={"primary"} className={classNames(css.icon, "icon-large")} />
          )}
        </div>
      </div>
      {submitOrderError && !orderId && (
        <OrderStatus
          statusText={"Bestilling feilet"}
          hasError={true}
          orderCountTotal={1}
          orderCountCompleted={0}
          orderCountCancelled={1}
          statusMessages={["Beklager feilen. Vennligst prøv igjen senere."]}
          isSafeToClose={false}
          safeToCloseMessages={[""]}
        />
      )}
      {!submitOrderError && (
        <OrderStatus
          statusText={
            isCompleted
              ? t("SingleOrderStatusChecker.ChangesDoneInfoText")
              : isCancelled
              ? t("SingleOrderStatusChecker.ChangesCancelledInfoText")
              : checkStatusFailed
              ? "Sjekk av ordrestatus feilet."
              : t("SingleOrderStatusChecker.ChangesProcessingInfoText")
          }
          hasError={checkStatusFailed}
          orderCountTotal={1}
          orderCountCompleted={isCompleted ? 1 : 0}
          orderCountCancelled={isCancelled ? 1 : 0}
          statusMessages={[
            isCompleted
              ? !!statusMessageOnCompletedGenerator
                ? statusMessageOnCompletedGenerator()
                : statusMessageOnCompleted ?? ""
              : isCancelled
              ? t("SingleOrderStatusChecker.OrderError")
              : checkStatusFailed
              ? ""
              : t("SingleOrderStatusChecker.OrderInProgress"),
          ]}
          isSafeToClose={true}
          safeToCloseMessages={
            isCompleted
              ? [t("SingleOrderStatusChecker.SafeToClose")]
              : isCancelled
              ? [statusError ?? t("SingleOrderStatusChecker.TryLater")]
              : !checkStatusFailed
              ? [t("SingleOrderStatusChecker.ChangesVisibleInfo"), t("SingleOrderStatusChecker.SafeToClose")]
              : [
                  "Beklager men vi kan dessverre ikke hente status på din ordre akkurat nå. ",
                  "Vennligst sjekk status senere og kontakt kundeservice om problemet vedvarer.",
                ]
          }
        />
      )}
    </div>
  )
}

const statusForValue = (value: string): OrderStatusId | undefined => {
  const key = Object.keys(OrderStatusId).find((r) => OrderStatusId[r] === value)
  if (!key) return undefined
  return OrderStatusId[key]
}
